import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Lock, Users, Plus, Eye, EyeOff,
  Trash2, Save, Shield, X, Copy, FileWarning, Clock, Activity, RotateCcw,
  Loader2, AlertTriangle, ChevronRight, Hash, Terminal, Share2, Zap,
  GitBranch, Globe, CheckCircle2, XCircle, RefreshCw, Server, Anchor, ArrowRight, Github ,Settings
} from 'lucide-react';
import { Formik, Form, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { authAPI, projectsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/ui/Modal';
import AuditLogs from '../../components/dashboard/AuditLogs';
import ServiceTokens from '../../components/dashboard/ServiceTokens';
import Button from '../../components/ui/Button';
import SEO from '../../components/shared/SEO';
import SecretsSkeleton from '../../components/ui/SecretsSkeleton';
import EnvBadge from '../../components/ui/EnvBdge';
import TabButton from '../../components/ui/TabButton';
import ExportModal from '../../components/dashboard/ExportModal';
import NetlifyIcon from '../../components/icons/netlify';
import JenkinsIcon from '../../components/icons/jenkins';
import RailwayIcon from '../../components/icons/railway';
import RenderIcon from '../../components/icons/render';
import CoolifyIcon from '../../components/icons/coolify';
import DockployIcon from '../../components/icons/dockploy';
import AwsSecretsManagerIcon from '../../components/icons/aws';
const PROVIDERS = [
  {
    id: 'vercel',
    name: 'Vercel',
    icon: <svg className="w-6 h-6 text-white" viewBox="0 0 1155 1000" fill="currentColor"><path d="M577.344 0L1154.69 1000H0L577.344 0Z" /></svg>,
    desc: 'Auto-sync secrets to Vercel deployments.',
    fields: [
      { name: 'apiToken', label: 'Vercel Access Token', type: 'password', placeholder: 'vc_...', optional: false, helpText: 'Create at Account Settings > Tokens.' },
      { name: 'projectId', label: 'Project ID', type: 'text', placeholder: 'prj_...', optional: false, helpText: 'Found in your Vercel Project Settings > General.' },
      { name: 'teamId', label: 'Team ID', type: 'text', placeholder: 'team_...', optional: true, helpText: 'Only needed if the project belongs to a Vercel Team. Found in Team Settings.' }
    ]
  },
  {
    id: 'netlify',
    name: 'Netlify',
    icon: <NetlifyIcon />, // Assuming you have this imported
    desc: 'Push variables to Netlify build settings.',
    fields: [
      { name: 'apiToken', label: 'Personal Access Token', type: 'password', placeholder: 'nfp_...', optional: false, helpText: 'Create at User Settings > Applications > Personal access tokens.' },
      { name: 'siteId', label: 'Site ID', type: 'text', placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx', optional: false, helpText: 'Found in your Netlify Site Settings > General > Site details.' }
    ]
  },
  {
    id: 'render',
    name: 'Render',
    icon: <RenderIcon />, // Assuming you have this imported
    desc: 'Update env vars for Render services.',
    fields: [
      { name: 'apiKey', label: 'API Key', type: 'password', placeholder: 'rnd_...', optional: false, helpText: 'Create at Account Settings > API Keys.' },
      { name: 'serviceId', label: 'Service ID', type: 'text', placeholder: 'srv-...', optional: false, helpText: 'Found in the URL of your Render service dashboard (starts with srv-).' }
    ]
  },
  {
    id: 'jenkins',
    name: 'Jenkins',
    icon: <JenkinsIcon />, // Assuming you have this imported
    desc: 'Sync secrets to Jenkins Credential Store.',
    fields: [
      { name: 'url', label: 'Jenkins URL', type: 'text', placeholder: 'https://jenkins.company.com', optional: false, helpText: 'The base URL of your Jenkins instance.' },
      { name: 'username', label: 'Username', type: 'text', placeholder: 'admin', optional: false, helpText: 'Your Jenkins login username.' },
      { name: 'apiToken', label: 'API Token', type: 'password', placeholder: '11xxxxxxxx', optional: false, helpText: 'Create at User > Configure > API Token > Add new Token.' }
    ]
  },
  {
    id: 'github',
    name: 'GitHub Actions',
    icon: <div className="text-white"><Github size={24} /></div>, // Assuming Lucide-react Github icon
    desc: 'Sync secrets to GitHub Repository Environments.',
    fields: [
      { name: 'pat', label: 'Personal Access Token', type: 'password', placeholder: 'ghp_...', optional: false, helpText: 'Create at Developer Settings > Personal access tokens. Must have the "repo" scope.' },
      { name: 'owner', label: 'Repository Owner', type: 'text', placeholder: 'e.g., octocat', optional: false, helpText: 'The GitHub username or Organization name.' },
      { name: 'repo', label: 'Repository Name', type: 'text', placeholder: 'e.g., hello-world', optional: false, helpText: 'The exact name of the repository.' },
      { name: 'environment', label: 'Target Environment', type: 'text', placeholder: 'e.g., production', optional: false, helpText: 'The name of the environment located in Repo Settings > Environments.' }
    ]
  },
  {
    id: 'aws',
    name: 'AWS Secrets Manager',
    icon: <AwsSecretsManagerIcon />, // Assuming you have this imported
    desc: 'Sync secrets securely to AWS Secrets Manager.',
    fields: [
      { name: 'region', label: 'AWS Region', type: 'text', placeholder: 'e.g., us-east-1', optional: false, helpText: 'Found in the top right corner of your AWS Management Console.' },
      { name: 'secretName', label: 'Secret Name', type: 'text', placeholder: 'e.g., production/myapp', optional: false, helpText: 'The exact name of the secret container in Secrets Manager.' },
      { name: 'accessKeyId', label: 'Access Key ID', type: 'text', placeholder: 'e.g., AKIAIOSFODNN7EXAMPLE', optional: false, helpText: 'Create via IAM > Users > Security credentials > Create access key.' },
      { name: 'apiToken', label: 'Secret Access Key', type: 'password', placeholder: 'e.g., wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY', optional: false, helpText: 'The secret key provided when you generated the Access Key.' }
    ]
  },
  // {
  //   id: 'railway',
  //   name: 'Railway',
  //   icon: <RailwayIcon />, // Assuming you have this imported
  //   desc: 'Sync variables to Railway projects.',
  //   fields: [
  //     { name: 'apiToken', label: 'Railway Token', type: 'password', placeholder: 'ry_...', optional: false, helpText: 'Create at Account Settings > Tokens.' },
  //     { name: 'projectId', label: 'Project ID', type: 'text', placeholder: '...', optional: false, helpText: 'Found in your Railway Project > Settings > General.' },
  //     { name: 'environment', label: 'Environment Name', type: 'text', placeholder: 'production', optional: false, helpText: 'The specific environment name (e.g., production, development).' }
  //   ]
  // },
  // {
  //   id: 'coolify',
  //   name: 'Coolify',
  //   icon: <CoolifyIcon />, // Assuming you have this imported
  //   desc: 'Push secrets to self-hosted Coolify.',
  //   fields: [
  //     { name: 'instanceUrl', label: 'Instance URL', type: 'text', placeholder: 'https://app.coolify.io', optional: false, helpText: 'The base URL where your Coolify dashboard is hosted.' },
  //     { name: 'apiToken', label: 'API Token', type: 'password', placeholder: '...', optional: false, helpText: 'Create at Coolify Settings > Keys & Tokens > API Tokens.' },
  //     { name: 'appUuid', label: 'Application UUID', type: 'text', placeholder: '...', optional: false, helpText: 'Found in the Application\'s General settings tab.' }
  //   ]
  // },
  // {
  //   id: 'dockploy',
  //   name: 'Dockploy',
  //   icon: <DockployIcon />, // Assuming you have this imported
  //   desc: 'Deploy secrets to Dockploy servers.',
  //   fields: [
  //     { name: 'instanceUrl', label: 'Console URL', type: 'text', placeholder: 'https://console.dockploy.com', optional: false, helpText: 'The base URL of your Dockploy instance.' },
  //     { name: 'apiKey', label: 'API Key', type: 'password', placeholder: '...', optional: false, helpText: 'Generated in your Dockploy User Settings.' },
  //     { name: 'serverId', label: 'Server/Project ID', type: 'text', placeholder: '...', optional: false, helpText: 'Found in the specific project dashboard URL.' }
  //   ]
  // },
  // {
  //   id: 'cloudflare',
  //   name: 'Cloudflare Workers',
  //   icon: <svg className="w-6 h-6 text-[#F38020]" viewBox="0 0 24 24" fill="currentColor"><path d="M19.46 7.64C19.16 5.56 17.38 4 15.2 4c-1.39 0-2.6.7-3.32 1.77C11.33 4.67 9.81 3.5 8 3.5 5.24 3.5 3 5.74 3 8.5c0 .24.03.48.07.71C1.35 9.87 0 11.53 0 13.5 0 15.98 2.02 18 4.5 18h15c2.48 0 4.5-2.02 4.5-4.5 0-2.3-1.68-4.21-3.87-4.48l-.67-1.38z" /></svg>,
  //   desc: 'Push secrets directly to Cloudflare Workers.',
  //   fields: [
  //     { name: 'accountId', label: 'Account ID', type: 'text', placeholder: 'e.g., 023e105f4...', optional: false, helpText: 'Found on the right sidebar in Workers & Pages > Overview.' },
  //     { name: 'scriptName', label: 'Worker Script Name', type: 'text', placeholder: 'e.g., my-api-worker', optional: false, helpText: 'The exact name of your deployed Worker.' },
  //     { name: 'apiToken', label: 'API Token', type: 'password', placeholder: 'e.g., cFL_...', optional: false, helpText: 'Create at My Profile > API Tokens. Needs "Worker Scripts: Edit" permissions.' }
  //   ]
  // },

  // --- NEW PROVIDERS ADDED BELOW ---

  {
    id: 'azure',
    name: 'Azure Key Vault',
    icon: <svg className="w-6 h-6 text-[#0078D4]" viewBox="0 0 24 24" fill="currentColor"><path d="M11.4 24l-5.6-8.9h5.1l3.5 5.5 3.5-5.5h5.1L17.4 24h-6zm-1.1-24h3.4l6.5 10.3h-4.8L12 4.8l-3.4 5.5H3.8L10.3 0z" /></svg>,
    desc: 'Sync variables securely to Azure Key Vault.',
    fields: [
      { name: 'vaultUrl', label: 'Vault URI', type: 'text', placeholder: 'https://myvault.vault.azure.net/', optional: false, helpText: 'Found on the Overview page of your Key Vault in the Azure Portal.' },
      { name: 'tenantId', label: 'Tenant ID', type: 'text', placeholder: '...', optional: false, helpText: 'Found in Entra ID (Azure AD) > Overview > Tenant ID.' },
      { name: 'clientId', label: 'Client ID', type: 'text', placeholder: '...', optional: false, helpText: 'Found in Entra ID > App Registrations > Overview > Application (client) ID.' },
      { name: 'apiToken', label: 'Client Secret', type: 'password', placeholder: '...', optional: false, helpText: 'Generated in App Registrations > Certificates & secrets > Client secrets. Make sure Key Vault Access Policies allow this App to set secrets.' }
    ]
  },
  // {
  //   id: 'google',
  //   name: 'Google Secret Manager',
  //   icon: <svg className="w-6 h-6 text-[#4285F4]" viewBox="0 0 24 24" fill="currentColor"><path d="M12 11h8.533c.044.385.067.78.067 1.184 0 2.734-.98 5.036-2.678 6.6-1.485 1.371-3.511 2.175-5.942 2.175A8.976 8.976 0 013 12a8.976 8.976 0 019-8.959c2.411 0 4.522.895 6.131 2.427l-2.613 2.613C14.61 7.12 13.435 6.6 12 6.6a5.373 5.373 0 00-5.353 5.4A5.373 5.373 0 0012 17.401c3.158 0 4.522-1.99 4.84-3.401H12v-3z" /></svg>,
  //   desc: 'Manage secrets across Google Cloud Platform.',
  //   fields: [
  //     { name: 'projectId', label: 'GCP Project ID', type: 'text', placeholder: 'e.g., my-project-12345', optional: false, helpText: 'Found in the GCP Console under Project info. Use the ID, not the Project Name.' },
  //     { name: 'apiToken', label: 'Service Account JSON', type: 'password', placeholder: 'Paste the entire contents of the JSON key file...', optional: false, helpText: 'Create a Service Account with "Secret Manager Admin" permissions. Create a new JSON key, download it, and paste its entire contents here.' }
  //   ]
  // }
];
// const PROVIDERS = [
//   {
//     id: 'vercel',
//     name: 'Vercel',
//     icon: <svg className="w-6 h-6 text-white" viewBox="0 0 1155 1000" fill="currentColor"><path d="M577.344 0L1154.69 1000H0L577.344 0Z" /></svg>,
//     desc: 'Auto-sync secrets to Vercel deployments.',
//     fields: [
//       { name: 'apiToken', label: 'Vercel Access Token', type: 'password', placeholder: 'vc_...', optional: false },
//       { name: 'projectId', label: 'Project ID', type: 'text', placeholder: 'prj_...', optional: false },
//       { name: 'teamId', label: 'Team ID', type: 'text', placeholder: 'team_...', optional: true }
//     ]
//   },
//   {
//     id: 'netlify',
//     name: 'Netlify',
//     icon: <NetlifyIcon />,
//     desc: 'Push variables to Netlify build settings.',
//     fields: [
//       { name: 'apiToken', label: 'Personal Access Token', type: 'password', placeholder: 'nfp_...', optional: false },
//       { name: 'siteId', label: 'Site ID', type: 'text', placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx', optional: false }
//     ]
//   },

//   {
//     id: 'render',
//     name: 'Render',
//     icon: <RenderIcon />,
//     desc: 'Update env vars for Render services.',
//     fields: [
//       { name: 'apiKey', label: 'API Key', type: 'password', placeholder: 'rnd_...', optional: false },
//       { name: 'serviceId', label: 'Service ID', type: 'text', placeholder: 'srv-...', optional: false }
//     ]
//   },
//   {
//     id: 'jenkins',
//     name: 'Jenkins',
//     icon: <JenkinsIcon />,
//     desc: 'Sync secrets to Jenkins Credential Store.',
//     fields: [
//       { name: 'url', label: 'Jenkins URL', type: 'text', placeholder: 'https://jenkins.company.com', optional: false },
//       { name: 'username', label: 'Username', type: 'text', placeholder: 'admin', optional: false },
//       { name: 'apiToken', label: 'API Token', type: 'password', placeholder: '11xxxxxxxx', optional: false }
//     ]
//   },
//   {
//     id: 'github',
//     name: 'GitHub Actions',
//     icon: <div className="text-white"><Github size={24} /></div>,
//     desc: 'Sync secrets to GitHub Repository Environments.',
//     fields: [
//       { name: 'pat', label: 'Personal Access Token', type: 'password', placeholder: 'ghp_...', optional: false },
//       { name: 'owner', label: 'Repository Owner', type: 'text', placeholder: 'e.g., octocat', optional: false },
//       { name: 'repo', label: 'Repository Name', type: 'text', placeholder: 'e.g., hello-world', optional: false },
//       { name: 'environment', label: 'Target Environment', type: 'text', placeholder: 'e.g., production', optional: false }
//     ]
//   },
//   {
//     id: 'aws',
//     name: 'AWS Secrets Manager',
//     icon: <AwsSecretsManagerIcon />,
//     desc: 'Sync secrets securely to AWS Secrets Manager.',
//     fields: [
//       { name: 'region', label: 'AWS Region', type: 'text', placeholder: 'e.g., us-east-1', optional: false },
//       { name: 'secretName', label: 'Secret Name', type: 'text', placeholder: 'e.g., production/myapp', optional: false },
//       { name: 'accessKeyId', label: 'Access Key ID', type: 'text', placeholder: 'e.g., AKIAIOSFODNN7EXAMPLE', optional: false },
//       { name: 'apiToken', label: 'Secret Access Key', type: 'password', placeholder: 'e.g., wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY', optional: false }
//     ]
//   },
//   {
//     id: 'railway',
//     name: 'Railway',
//     icon: <RailwayIcon />,
//     desc: 'Sync variables to Railway projects.',
//     fields: [
//       { name: 'apiToken', label: 'Railway Token', type: 'password', placeholder: 'ry_...', optional: false },
//       { name: 'projectId', label: 'Project ID', type: 'text', placeholder: '...', optional: false },
//       { name: 'environment', label: 'Environment Name', type: 'text', placeholder: 'production', optional: false }
//     ]
//   },
//   {
//     id: 'coolify',
//     name: 'Coolify',
//     icon: <CoolifyIcon />,
//     desc: 'Push secrets to self-hosted Coolify.',
//     fields: [
//       { name: 'instanceUrl', label: 'Instance URL', type: 'text', placeholder: 'https://app.coolify.io', optional: false },
//       { name: 'apiToken', label: 'API Token', type: 'password', placeholder: '...', optional: false },
//       { name: 'appUuid', label: 'Application UUID', type: 'text', placeholder: '...', optional: false }
//     ]
//   },
//   {
//     id: 'dockploy',
//     name: 'Dockploy',
//     icon: <DockployIcon />,
//     desc: 'Deploy secrets to Dockploy servers.',
//     fields: [
//       { name: 'instanceUrl', label: 'Console URL', type: 'text', placeholder: 'https://console.dockploy.com', optional: false },
//       { name: 'apiKey', label: 'API Key', type: 'password', placeholder: '...', optional: false },
//       { name: 'serverId', label: 'Server/Project ID', type: 'text', placeholder: '...', optional: false }
//     ]
//   },
//   {
//     id: 'cloudflare',
//     name: 'Cloudflare Workers',
//     icon: <svg className="w-6 h-6 text-[#F38020]" viewBox="0 0 24 24" fill="currentColor"><path d="M19.46 7.64C19.16 5.56 17.38 4 15.2 4c-1.39 0-2.6.7-3.32 1.77C11.33 4.67 9.81 3.5 8 3.5 5.24 3.5 3 5.74 3 8.5c0 .24.03.48.07.71C1.35 9.87 0 11.53 0 13.5 0 15.98 2.02 18 4.5 18h15c2.48 0 4.5-2.02 4.5-4.5 0-2.3-1.68-4.21-3.87-4.48l-.67-1.38z" /></svg>,
//     desc: 'Push secrets directly to Cloudflare Workers.',
//     fields: [
//       { name: 'accountId', label: 'Account ID', type: 'text', placeholder: 'e.g., 023e105f4...', optional: false },
//       { name: 'scriptName', label: 'Worker Script Name', type: 'text', placeholder: 'e.g., my-api-worker', optional: false },
//       { name: 'apiToken', label: 'API Token', type: 'password', placeholder: 'e.g., cFL_...', optional: false }
//     ]
//   },
// ];
export default function ProjectDetails() {
  const { id } = useParams();
  const { user, usage, setUsage, setUser } = useAuth();
  const navigate = useNavigate()

  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isEnvLoading, setIsEnvLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('secrets');
  const [activeEnv, setActiveEnv] = useState('dev');
  const [revealedKeys, setRevealedKeys] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [isInviteOpen, setInviteOpen] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [project, setProject] = useState(null);
  const [secrets, setSecrets] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(0);
  const [history, setHistory] = useState([]);
  const [userToRemove, setUserToRemove] = useState(null);
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [loadIntegrations, setLoadIntegrations] = useState(false)

  // --- INTEGRATION STATE ---
  const [integrations, setIntegrations] = useState([]);
  const [isIntegrationModalOpen, setIntegrationModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [integrationToDelete, setIntegrationToDelete] = useState(null)
  const [showIntegrationLog, setShowIntegrationLog] = useState(null)
  const [expandedIntegration, setExpandedIntegration] = useState(null);

  const currentUserMember = project?.members?.find(m => m.user._id === user?._id);
  const myRole = currentUserMember?.role || 'viewer';
  const isAdmin = myRole === 'admin';
  const canEditSecrets = ['admin', 'editor'].includes(myRole);

  const fetchUser = async () => {
    const data = await authAPI.getMe();
    if (data) {
      setUser(data)
    }
  };

  const fetchProjectInfo = async () => {
    try {
      const data = await projectsAPI.getOne(id);
      setProject(data);
    } catch (error) { console.error("Failed to load project"); }
  };

  const fetchIntegrations = useCallback(async () => {
    try {
      setLoadIntegrations(true)
      const data = await projectsAPI.getIntegrations(id);
      setIntegrations(data);
    } catch (error) {
      console.error("Failed to load integrations", error);
      toast.error("Could not fetch integrations");
    } finally {
      setLoadIntegrations(false)
    }
  }, [id]);

  const fetchUsage = async () => {
    const data = await authAPI.usage();
    if (data) setUsage(data)
  };

  useEffect(() => { fetchUsage(); fetchUser() }, [])
  useEffect(() => { if (activeTab == 'integrations') fetchIntegrations() }, [activeTab])

  useEffect(() => {
    let mounted = true;
    const init = async () => {
      if (!user || !id) return;
      try {
        const [projData, secData] = await Promise.all([
          projectsAPI.getOne(id),
          projectsAPI.getSecrets(id, 'dev')
        ]);
        if (mounted) {
          setProject(projData);
          setSecrets(secData.variables || []);
          setCurrentVersion(secData.version || 0);
          setIsPageLoading(false);
          fetchIntegrations(); // Fetch integrations
        }
      } catch (error) { if (mounted) setIsPageLoading(false); }
    };
    init();
    return () => { mounted = false; };
  }, [id, user, fetchIntegrations]);

  const openRemoveModal = (userId) => {
    setUserToRemove(userId);
    setConfirmOpen(true);
  };

  const handleEnvChange = useCallback(async (newEnv) => {
    if (newEnv === activeEnv) return;
    setActiveEnv(newEnv);
    setIsEnvLoading(true);
    setSecrets([]);
    setIsEditMode(false);
    try {
      const data = await projectsAPI.getSecrets(id, newEnv);
      setSecrets(data.variables || []);
      setCurrentVersion(data.version || 0);
    } catch (error) {
      toast.error(`Failed to load ${newEnv} secrets`);
    } finally { setIsEnvLoading(false); }
  }, [activeEnv, id]);

  const handleSaveSecrets = async (values) => {
    const cleanSecrets = values.secrets.filter(s => s.key.trim() !== '');
    const keys = cleanSecrets.map(s => s.key.trim());
    const uniqueKeys = new Set(keys);

    if (uniqueKeys.size !== keys.length) {
      const seen = new Set();
      const duplicates = keys.filter(k => seen.size === seen.add(k).size);
      toast.error(`Duplicate Key Detected: ${duplicates[0]}`);
      return;
    }

    const loadingToast = toast.loading("Encrypting Vault...");
    try {
      await projectsAPI.updateSecrets(id, activeEnv, cleanSecrets);
      const data = await projectsAPI.getSecrets(id, activeEnv);
      setSecrets(data.variables);
      setCurrentVersion(data.version);
      setIsEditMode(false);
      toast.dismiss(loadingToast);
      toast.success("Vault Synchronized");

      // OPTIONAL: Refresh integrations status after push
      fetchIntegrations();

    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("Encryption Failed");
    }
  };

  const handleRollback = async (version) => {
    const loadId = toast.loading("Restoring Version...");
    try {
      await projectsAPI.rollback(id, activeEnv, version);
      const data = await projectsAPI.getSecrets(id, activeEnv);
      setSecrets(data.variables);
      setCurrentVersion(data.version);
      setShowHistoryModal(false);
      toast.dismiss(loadId);
      toast.success(`Restored to v${version}`);
    } catch (error) {
      toast.dismiss(loadId);
      toast.error("Rollback failed");
    }
  };

  const handleOpenHistory = async () => {
    setShowHistoryModal(true);
    try {
      const data = await projectsAPI.getHistory(id, activeEnv);
      setHistory(data);
    } catch (error) { toast.error("History Access Denied"); }
  };

  const handleInvite = async (values, { resetForm }) => {
    await projectsAPI.invite(id, values);
    fetchProjectInfo();
    setInviteOpen(false);
    resetForm();
    toast.success("Member Added");
  };

  const handleRoleUpdate = async (userId, newRole) => {
    await projectsAPI.updateRole(id, userId, newRole);
    fetchProjectInfo();
    toast.success("Permission Updated");
  };

  const handleConfirmRemove = async () => {
    try {
      await projectsAPI.removeMember(id, userToRemove);
      await fetchProjectInfo();
      toast.success("Access Revoked");
    } catch (error) {
      toast.error("Failed to remove member");
    } finally {
      setConfirmOpen(false);
      setUserToRemove(null);
    }
  };

  // --- INTEGRATION HANDLERS ---
  const handleConnectIntegration = (provider) => {
    setSelectedProvider(provider);
    setIntegrationModalOpen(true);
  };

  const handleSubmitIntegration = async (values, { setSubmitting, resetForm }) => {
    try {
      // Prepare payload
      const payload = {
        provider: selectedProvider.id,
        envdock_env: values.envdock_env,
        config: values // specific provider fields
      };

      // Call your backend API
      const data = await projectsAPI.addIntegration(id, payload);
      console.log("DATA", data)

      // Mock success for UI
      if (data)
        setIntegrations([...integrations, {
          ...data
        }]);

      toast.success(`Connected to ${selectedProvider.name}`);
      setIntegrationModalOpen(false);
      resetForm();
    } catch (error) {
      toast.error("Integration failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleIntegrationDelete = async () => {
    if (!integrationToDelete) return;
    try {
      const data = await projectsAPI.deleteIntegration(id, integrationToDelete._id);
      if (data.success)
        setIntegrations((prev) =>
          prev.filter((integration) => integration._id !== integrationToDelete._id)
        );
      toast.success("Integration removed successfully");
      setIntegrationToDelete(null)
    } catch (error) {
      console.error("Delete Integration Error:", error);
      toast.error("Failed to remove integration");
    }
  };

  if (isPageLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-gray-700 gap-6">
        <div className="w-12 h-12 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin" />
        <p className="font-black uppercase tracking-[0.4em] text-[10px] animate-pulse px-4 text-center">Establishing Secure Tunnel</p>
      </div>
    );
  }

  if (!project) return <div className="text-center py-20 font-black text-red-600 uppercase tracking-widest">Vault Not Found</div>;

  return (
    <>
      <SEO title="Project Vault | Environment Explorer" description="Modify, sync, and audit specific environment variables for this project cluster." noIndex={true} />

      <div className="max-w-6xl mx-auto space-y-6 md:space-y-10 pb-24 px-0">

        {/* --- Breadcrumb & Actions --- */}
        <div className="flex flex-col gap-6 md:gap-8 pt-4">
          <Link to="/dashboard/projects" className="group inline-flex items-center text-gray-600 hover:text-orange-500 text-[10px] font-black uppercase tracking-[0.2em] transition-all w-fit">
            <ArrowLeft size={14} className="mr-2 group-hover:-translate-x-1 transition-transform" /> Back to Infrastructure
          </Link>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 md:gap-8">
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-3xl pr-1 md:text-4xl font-black text-white tracking-tighter max-w-full min-w-0 line-clamp-2 break-words">
                  {project.name}
                </h1>
                <span className={`px-3 py-0.5 rounded-full border text-[9px] font-black uppercase tracking-[0.2em] shadow-sm ${isAdmin ? 'bg-orange-500/10 border-orange-500/20 text-orange-500' :
                  canEditSecrets ? 'bg-blue-500/10 border-blue-500/20 text-blue-400' :
                    'bg-white/5 border-white/10 text-gray-500'
                  }`}>
                  {myRole}
                </span>
              </div>
              <div className="font-mono text-[11px] text-gray-600 tracking-wide break-all">
                ID: {project._id}
              </div>
              <p className="text-gray-500 font-medium text-sm md:text-base max-w-2xl line-clamp-2">{project.description || "Production infrastructure vault."}</p>
            </div>
          </div>
          <div className="flex items-end justify-end gap-6 md:gap-8">
            <div className="flex items-center w-full lg:w-auto bg-[#0a0a0a] p-1 rounded-2xl border border-white/5 shadow-inner overflow-x-auto no-scrollbar">
              <TabButton
                active={activeTab === 'secrets'}
                onClick={() => setActiveTab('secrets')}
                icon={Lock}
                label="Vault"
                disabled={isEditMode}
              />
              <TabButton
                active={activeTab === 'team'}
                onClick={() => setActiveTab('team')}
                icon={Users}
                label="Team"
                disabled={isEditMode}
              />
              {/* Integrations Tab with usage check */}
              <TabButton
                active={activeTab === 'integrations'}
                onClick={() => {
                  setActiveTab('integrations');
                  // if (!(project.owner == user._id) || !usage?.limits?.integrations) {
                  //   // Optional: Trigger an upgrade modal or toast here
                  //   toast.error("Integrations are a Pro feature");
                  // } else {
                  //   setActiveTab('integrations');
                  // }
                }}
                icon={Zap}
                label="Integrations"
                disabled={isEditMode}
              />

              <TabButton
                active={activeTab === 'logs'}
                onClick={() => setActiveTab('logs')}
                icon={Activity}
                label="Logs"
                disabled={isEditMode}
              />
            </div>
          </div>
        </div>

        {/* --- Main Dashboard View --- */}
        <div className="bg-[#0a0a0a] border border-white/5 rounded-[1.5rem] md:rounded-[2.5rem] min-h-[450px] md:min-h-[550px] overflow-hidden shadow-2xl relative">
          {/* === THE PAYWALL INTERCEPTOR === */}
          {project?.status === 'locked' ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[450px] md:min-h-[550px] p-6 text-center relative overflow-hidden">
              {/* Background Glow */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-red-600/10 rounded-full blur-[100px] pointer-events-none" />

              <div className="relative z-10 max-w-xl mx-auto flex flex-col items-center">
                <div className="w-20 h-20 bg-red-500/10 border border-red-500/20 rounded-3xl flex items-center justify-center mb-6 text-red-500 shadow-2xl shadow-red-500/20">
                  <Lock size={40} className="fill-red-500/20" />
                </div>

                <h2 className="text-3xl md:text-4xl font-black text-white tracking-tighter mb-4 uppercase">
                  Vault is <span className="text-red-500">Locked</span>
                </h2>

                <p className="text-sm md:text-base text-gray-400 mb-8 leading-relaxed font-medium">
                  The Pro subscription for this infrastructure has expired. Your secrets are securely frozen, but read/write access and CI/CD pipelines have been suspended.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full mb-10">
                  <div className="flex items-center gap-3 bg-white/[0.02] border border-white/5 p-4 rounded-2xl text-left">
                    <XCircle size={18} className="text-red-500 shrink-0" />
                    <span className="text-xs font-bold text-gray-300 uppercase tracking-widest">CLI Pull/Push Blocked</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white/[0.02] border border-white/5 p-4 rounded-2xl text-left">
                    <XCircle size={18} className="text-red-500 shrink-0" />
                    <span className="text-xs font-bold text-gray-300 uppercase tracking-widest">Integrations Paused</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white/[0.02] border border-white/5 p-4 rounded-2xl text-left">
                    <XCircle size={18} className="text-red-500 shrink-0" />
                    <span className="text-xs font-bold text-gray-300 uppercase tracking-widest">Dashboard View Hidden</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white/[0.02] border border-white/5 p-4 rounded-2xl text-left">
                    <XCircle size={18} className="text-red-500 shrink-0" />
                    <span className="text-xs font-bold text-gray-300 uppercase tracking-widest">Team Sync Disabled</span>
                  </div>
                </div>

                <Link to="/dashboard/subscription" className="w-full sm:w-auto">
                  <Button variant="primary" className="w-full px-12 py-4 bg-white text-black hover:bg-orange-500 hover:text-white font-black uppercase text-xs tracking-widest transition-all shadow-xl shadow-white/10 hover:shadow-orange-500/20">
                    Upgrade to Pro to Unlock
                  </Button>
                </Link>

                <p className="text-[10px] text-gray-600 font-bold uppercase tracking-widest mt-6">
                  Or delete an active project to trigger auto-unlock
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* === SECRETS / VAULT TAB === */}
              {activeTab === 'secrets' && (
                <div className="flex flex-col h-full">

                  {/* Toolbar */}
                  <div className="sticky top-0 z-30 flex flex-col lg:flex-row md:items-center justify-between p-4 md:p-6 border-b border-white/5 bg-[#0a0a0a]/80 backdrop-blur-xl gap-4 custom-scrollbar">
                    <div className="flex items-center gap-4 overflow-x-auto custom-scrollbar w-full lg:w-auto pb-1 md:pb-0">
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-700 whitespace-nowrap">ENV:</span>
                      <div className="flex gap-2 justify-start">
                        {project.environments.map(env => (
                          <EnvBadge
                            key={env}
                            label={env}
                            active={activeEnv === env}
                            onClick={() => handleEnvChange(env)}
                            disabled={isEditMode || isEnvLoading}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    {!isEnvLoading && canEditSecrets && (
                      <div className="flex items-center justify-between md:justify-end gap-3 w-full lg:w-auto border-t lg:border-t-0 border-white/5 pt-3 lg:pt-0">
                        {!isEditMode ? (
                          <div className="flex items-center gap-3 w-full md:w-auto">
                            {secrets?.length > 0 && <>   <button
                              type='button'
                              onClick={() => setIsExportOpen(true)}
                              className="h-10 px-4 flex items-center justify-center text-gray-600 hover:text-white hover:bg-white/5 rounded-xl transition-all border border-white/5"
                              title="Export Variables"
                            >
                              <Share2 size={16} className="md:mr-2" />
                              <span className="text-[10px] font-black uppercase tracking-widest md:inline hidden">Export</span>
                            </button>
                              <button
                                type='button'
                                onClick={handleOpenHistory}
                                className=" h-10 px-4 flex items-center justify-center text-gray-600 hover:text-white hover:bg-white/5 rounded-xl transition-all border border-white/5"
                              >
                                <Clock size={16} className="md:mr-2" />
                                <span className="text-[10px] font-black uppercase tracking-widest md:inline hidden">Timeline</span>
                              </button>
                            </>}
                            <Button
                              type="button"
                              variant="primary"
                              className="flex-1 md:flex-none h-10 px-6"
                              onClick={() => setIsEditMode(true)}
                            >
                              <span className="text-[10px] font-black uppercase tracking-widest">Edit Vault</span>
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 w-full md:w-auto">
                            <Button
                              type="button"
                              variant="outline"
                              className="flex-1 md:flex-none h-10 px-4"
                              onClick={() => { setIsEditMode(false); }}
                            >
                              <span className="text-[10px] font-black uppercase tracking-widest">Discard</span>
                            </Button>
                            <div className="hidden md:block">
                              <Button
                                type="submit"
                                form="vault-form"
                                variant="primary"
                                className="h-10 px-6"
                              >
                                <Save size={14} className="mr-2" />
                                <span className="text-[10px] font-black uppercase tracking-widest">Push</span>
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Form Content Area */}
                  {isEnvLoading ? (
                    <SecretsSkeleton />
                  ) : (
                    <Formik
                      initialValues={{ secrets: secrets }}
                      enableReinitialize={true}
                      onSubmit={handleSaveSecrets}
                    >
                      {({ values, resetForm, isSubmitting }) => (
                        <Form id="vault-form" className="flex-1">
                          <div className="p-4 md:p-12 pb-32 md:pb-12">
                            <FieldArray name="secrets">
                              {({ remove, push }) => (
                                <div className="space-y-4">
                                  {values.secrets?.length === 0 && !isEditMode && (
                                    <div className="flex flex-col items-center justify-center py-20 text-gray-700 border border-dashed border-white/5 rounded-2xl md:rounded-[2rem]">
                                      <Terminal size={40} className="mb-4 opacity-20" />
                                      <p className="text-[9px] font-black uppercase tracking-[0.2em]">Vault is Empty</p>
                                    </div>
                                  )}

                                  {values.secrets?.map((secret, index) => (
                                    <motion.div
                                      key={index}
                                      layout
                                      initial={{ opacity: 0, x: -10 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      className="flex flex-col md:flex-row gap-3 md:gap-4 items-start md:items-center group p-3 md:p-0 bg-white/[0.02] md:bg-transparent rounded-2xl md:rounded-none border border-white/5 md:border-none"
                                    >
                                      {/* Key Input */}
                                      <div className="w-full md:w-1/3">
                                        <Field
                                          name={`secrets.${index}.key`}
                                          placeholder="VARIABLE_KEY"
                                          disabled={!isEditMode}
                                          className={`w-full bg-black/40 md:bg-white/[0.02] border rounded-xl md:rounded-2xl px-4 py-3 font-mono text-[11px] outline-none transition-all
                                      ${isEditMode
                                              ? 'border-white/10 focus:border-orange-500 text-white focus:ring-4 focus:ring-orange-500/5'
                                              : 'border-transparent text-orange-500/80'}`}
                                        />
                                      </div>

                                      {/* Value Input */}
                                      <div className="relative w-full md:w-1/2">
                                        <Field
                                          name={`secrets.${index}.value`}
                                          type={revealedKeys[index] || isEditMode ? "text" : "password"}
                                          disabled={!isEditMode}
                                          placeholder="••••••••••••"
                                          className={`w-full bg-black/40 md:bg-white/[0.02] border rounded-xl md:rounded-2xl px-4 py-3 font-mono text-[11px] outline-none transition-all pr-12
                                      ${isEditMode
                                              ? 'border-white/10 focus:border-orange-500 text-white focus:ring-4 focus:ring-orange-500/5'
                                              : 'border-transparent text-gray-600'}`}
                                        />
                                        {!isEditMode && (
                                          <button
                                            type="button"
                                            onClick={() => setRevealedKeys(prev => ({ ...prev, [index]: !prev[index] }))}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-700 hover:text-white transition-colors"
                                          >
                                            {revealedKeys[index] ? <EyeOff size={14} /> : <Eye size={14} />}
                                          </button>
                                        )}
                                      </div>

                                      {/* Row Actions */}
                                      <div className="flex w-full md:w-auto justify-end md:justify-start gap-2">
                                        {isEditMode ? (
                                          <button type="button" onClick={() => remove(index)} className="w-9 h-9 flex items-center justify-center text-red-900 hover:text-red-500 hover:bg-red-500/5 rounded-lg transition-all">
                                            <Trash2 size={16} />
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            onClick={() => { navigator.clipboard.writeText(secret.value); toast.success("Copied"); }}
                                            className="w-9 h-9 flex items-center justify-center text-gray-800 hover:text-white hover:bg-white/5 rounded-lg md:opacity-0 group-hover:opacity-100 transition-all"
                                          >
                                            <Copy size={16} />
                                          </button>
                                        )}
                                      </div>
                                    </motion.div>
                                  ))}

                                  {isEditMode && (
                                    <button
                                      type="button"
                                      onClick={() => push({ key: '', value: '' })}
                                      className="flex items-center justify-center md:justify-start gap-3 text-orange-500 hover:text-orange-400 text-[10px] font-black uppercase tracking-[0.2em] mt-8 px-6 py-4 rounded-2xl bg-orange-500/5 border border-orange-500/10 hover:border-orange-500/30 transition-all w-full md:w-fit"
                                    >
                                      <Plus size={14} /> Add New Variable
                                    </button>
                                  )}
                                </div>
                              )}
                            </FieldArray>
                          </div>
                          {/* Mobile Only: Fixed Floating Save Button when editing */}
                          {isEditMode && (
                            <div className="fixed bottom-6 left-4 right-4 z-50 md:hidden">
                              <Button
                                type="submit"
                                variant="primary"
                                className="w-full h-14 shadow-2xl shadow-orange-500/40 flex items-center justify-center"
                                disabled={isSubmitting}
                              >
                                {isSubmitting ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} className="mr-2" />}
                                <span className="text-xs font-black uppercase tracking-widest">Commit Changes</span>
                              </Button>
                            </div>
                          )}
                        </Form>
                      )}
                    </Formik>
                  )}
                </div>
              )}

              {/* === INTEGRATIONS TAB === */}
              {activeTab === 'integrations' && (
                <div className="p-6 md:p-14 space-y-12">

                  <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div>
                      <h3 className="text-xl md:text-2xl font-black text-white tracking-tighter mb-2">Continuous Delivery</h3>
                      <p className="text-gray-500 font-medium text-xs md:text-sm max-w-xl">
                        Automatically push encrypted secrets to your infrastructure providers whenever your vault get updated.
                      </p>
                    </div>
                  </div>

                  {/* ACTIVE INTEGRATIONS LIST */}
                  {integrations?.length > 0 ? (
                    <div className="space-y-4">
                      <div className='flex flex-col md:flex-row justify-between items-center'>
                        <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-600 mb-4 md:mb-0">Active Pipelines</h4>

                        {
                          user?.subscriptionStatus === "deactivated" ?
                            <p className='text-[10px] font-black text-red-500 uppercase bg-white/[0.02] border border-white/5 rounded-xl px-4 py-4 md:py-2'>Sync paused: Integrations are configured, but the project owner's Pro subscription is inactive.</p> :
                            <button
                              onClick={fetchIntegrations}
                              className="flex items-center justify-center gap-2 px-4 py-4 md:py-2 bg-white/[0.02] border border-white/5 rounded-xl text-[10px] font-black text-orange-500 uppercase tracking-widest hover:bg-orange-500/10 transition-all w-full md:w-auto"
                            >
                              <RefreshCw size={12} className={loadIntegrations ? "animate-spin" : ""} /> Refresh Results
                            </button>

                        }
                      </div>
                      {integrations.map((int) => {
                        const providerDetails = PROVIDERS.find((p) => p.id === int.provider) || {};
                        const isExpanded = expandedIntegration === int._id;

                        return (
                          <div
                            key={int._id}
                            className="flex flex-col bg-white/[0.02] border border-white/5 rounded-2xl transition-all overflow-hidden"
                          >
                            {/* --- MAIN ROW --- */}
                            <div className="flex flex-col md:flex-row items-center justify-between p-5 gap-4">
                              <div className="flex items-center gap-4 w-full md:w-auto">
                                <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center border border-white/5">
                                  {providerDetails.icon}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h5 className="font-bold text-white text-sm">{providerDetails.name}</h5>
                                    <span className="text-[9px] px-2 py-0.5 rounded bg-white/10 text-gray-400 font-mono uppercase">
                                      {int.envdock_env}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-1">
                                    {int?.lastSyncStatus === 'failed' ? (
                                      <span className="flex items-center gap-1 text-[10px] text-red-500 font-bold uppercase">
                                        <XCircle size={10} /> Failed
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1 text-[10px] text-green-500 font-bold uppercase">
                                        <CheckCircle2 size={10} /> {int?.lastSyncStatus}
                                      </span>
                                    )}
                                    <span className="text-[10px] text-gray-600">
                                      • {new Date(int.lastSyncAt || int?.createdAt).toLocaleTimeString()}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* --- ACTIONS --- */}
                              <div className="flex items-center gap-3 w-full md:w-auto justify-end">
                                {int?.lastSyncStatus === 'failed' && (
                                  <button
                                    onClick={() => setShowIntegrationLog(int?.lastSyncError)}
                                    className="text-[10px] font-bold uppercase tracking-widest text-red-500 hover:text-white px-3 py-2 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-colors"
                                  >
                                    View Error
                                  </button>
                                )}

                                {/* NEW: View Config Toggle Button */}
                                <button
                                  onClick={() => setExpandedIntegration(isExpanded ? null : int._id)}
                                  className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-3 py-2 rounded-lg transition-colors ${isExpanded ? 'bg-orange-500/10 text-orange-500' : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
                                    }`}
                                >
                                  <Settings size={14} /> {isExpanded ? 'Hide' : 'Config'}
                                </button>

                                {project.owner === user._id && (
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setIntegrationToDelete(int);
                                    }}
                                    className="text-gray-600 hover:text-red-500 p-2 transition-colors"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* --- EXPANDED CONFIG DETAILS --- */}
                            {isExpanded && (
                              <div className="p-5 border-t border-white/5 bg-black/20">
                                <h6 className="text-[9px] font-black uppercase tracking-[0.2em] text-gray-600 mb-4">
                                  Configuration Details
                                </h6>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                  {providerDetails.fields?.map((field) => {
                                    // Extract the value from wherever your backend saves it (e.g., int.settings map/object)
                                    // Adjust `int.settings?.[field.name]` if your backend stores it directly on `int`
                                    const rawValue = int.settings?.[field.name] || int[field.name];

                                    // Mask the value if it's a password type, otherwise show the text (or N/A)
                                    const displayValue = field.type === 'password'
                                      ? '••••••••••••••••••••'
                                      : (rawValue || 'Not Provided');

                                    return (
                                      <div key={field.name} className="bg-white/5 border border-white/5 rounded-xl p-3">
                                        <p className="text-[9px] font-black uppercase tracking-widest text-gray-500 mb-1">
                                          {field.label}
                                        </p>
                                        <p className="text-xs text-white font-mono truncate" title={displayValue}>
                                          {displayValue}
                                        </p>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* {integrations.map((int) => {
                        const providerDetails = PROVIDERS.find(p => p.id === int.provider) || {};
                        return (
                          <div key={int._id} className="flex flex-col md:flex-row items-center justify-between p-5 bg-white/[0.02] border border-white/5 rounded-2xl gap-4">
                            <div className="flex items-center gap-4 w-full md:w-auto">
                              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center border border-white/5">
                                {providerDetails.icon}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h5 className="font-bold text-white text-sm">{providerDetails.name}</h5>
                                  <span className="text-[9px] px-2 py-0.5 rounded bg-white/10 text-gray-400 font-mono uppercase">{int.envdock_env}</span>
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  {int?.lastSyncStatus === 'failed' ? (
                                    <span className="flex items-center gap-1 text-[10px] text-red-500 font-bold uppercase"><XCircle size={10} /> Failed</span>
                                  ) : (
                                    <span className="flex items-center gap-1 text-[10px] text-green-500 font-bold uppercase"><CheckCircle2 size={10} /> {int?.lastSyncStatus}</span>
                                  )}
                                  <span className="text-[10px] text-gray-600">• {new Date(int.lastSyncAt || int?.createdAt).toLocaleTimeString()}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 w-full md:w-auto justify-end">
                              {int?.lastSyncStatus === 'failed' && <button onClick={() => { setShowIntegrationLog(int?.lastSyncError) }} className="text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-white px-3 py-2 bg-white/5 rounded-lg transition-colors">
                                View Logs
                              </button>}
                              {project.owner === user._id && <button onClick={(e) => {
                                e.preventDefault();
                                setIntegrationToDelete(int);
                              }} className="text-gray-600 hover:text-red-500 p-2 transition-colors">
                                <Trash2 size={16} />
                              </button>}
                            </div>
                          </div>
                        )
                      })} */}
                    </div>
                  )
                    :
                    <div className="flex flex-col items-center justify-center py-20 text-gray-700 border border-dashed border-white/5 rounded-2xl md:rounded-[2rem]">
                      <Terminal size={40} className="mb-4 opacity-20" />
                      <p className="text-[9px] font-black uppercase tracking-[0.2em]">No integrations yet.</p>
                    </div>}

                  {/* AVAILABLE PROVIDERS GRID */}
                  {isAdmin && <div className="space-y-4">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-600 mb-4">Available Providers</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {PROVIDERS.map((provider) => (
                        <button
                          key={provider.id}
                          onClick={() => handleConnectIntegration(provider)}
                          className="group flex flex-col p-6 bg-white/[0.02] border border-white/5 hover:border-orange-500/30 hover:bg-white/[0.04] rounded-2xl text-left transition-all relative overflow-hidden"
                        >
                          <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                            <ArrowRight size={16} className="text-orange-500 -rotate-45 group-hover:rotate-0 transition-transform duration-300" />
                          </div>
                          <div className="w-10 h-10 rounded-lg bg-[#111] border border-white/10 flex items-center justify-center mb-4 text-gray-400 group-hover:text-white transition-colors">
                            {provider.icon}
                          </div>
                          <h4 className="text-white font-bold mb-1">{provider.name}</h4>
                          <p className="text-xs text-gray-500 leading-relaxed">{provider.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>}
                </div>
              )}

              {/* === TEAM TAB === */}
              {activeTab === 'team' && (
                <div className="p-6 md:p-14 space-y-8 md:space-y-12">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-xl md:text-2xl font-black text-white tracking-tighter">Collaborators</h3>
                      <p className="text-gray-500 font-medium text-xs md:text-sm">Team members with vault clearance.</p>
                    </div>

                    {isAdmin && <>{usage?.limits?.teammates > project.members.length ?
                      <Button onClick={() => setInviteOpen(true)} variant="primary" size="sm" className="w-full md:w-auto">
                        Add Member
                      </Button> :
                      <div className="flex items-center justify-between lg:justify-start gap-3 px-4 py-2 bg-[#0d0d0d] border border-orange-500/20 rounded-xl group transition-all hover:border-orange-500/40 w-full lg:w-auto">

                        <div className="flex items-center gap-3">
                          <div className="flex shrink-0 w-7 h-7 rounded-lg bg-orange-500/10 items-center justify-center">
                            <Zap size={13} className="text-orange-500 animate-pulse" />
                          </div>

                          <div className="flex flex-col lg:flex-row lg:items-center gap-0 lg:gap-3">
                            <p className="text-[10px] lg:text-[11px] font-bold text-gray-400 uppercase tracking-widest leading-none">
                              Limit Reached
                            </p>
                            {/* Hidden on mobile, shown on desktop */}
                            <div className="hidden lg:block w-1 h-1 rounded-full bg-gray-700" />
                            <button
                              onClick={() => navigate('/dashboard/subscription')}
                              className="text-[10px] lg:text-[11px] text-orange-500 font-black uppercase tracking-tighter hover:text-orange-400 transition-colors flex items-center gap-1 mt-1 lg:mt-0"
                            >
                              Upgrade <ArrowRight size={12} className="hidden lg:inline" />
                            </button>
                          </div>
                        </div>

                        {/* Mobile-only Arrow (Keeps it compact) */}
                        <ArrowRight size={14} className="text-orange-500 lg:hidden" />
                      </div>} </>
                    }
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {project.members.map((member) => {
                      const isMe = member.user._id === user._id;
                      return (
                        <div key={member.user._id} className="flex flex-col md:flex-row md:items-center justify-between p-5 md:p-6 bg-white/[0.02] border border-white/5 rounded-2xl md:rounded-[2rem] gap-4 md:gap-6">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-[#111] border border-white/5 flex items-center justify-center text-white font-black overflow-hidden shrink-0">
                              {member.user.avatar ? <img src={member.user.avatar} alt="av" className="w-full h-full object-cover" /> : member.user.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="font-black text-white text-sm md:text-base truncate flex items-center gap-2">
                                {member.user.name}
                                {isMe && <span className="text-[8px] bg-orange-500 text-white px-2 py-0.5 rounded-full">YOU</span>}
                              </p>
                              <p className="text-[10px] md:text-xs text-gray-600 truncate">{member.user.email}</p>
                            </div>
                          </div>

                          <div className="flex items-center justify-between md:justify-end gap-4 border-t md:border-t-0 border-white/5 pt-3 md:pt-0">
                            {isAdmin && !isMe ? (
                              <select
                                value={member.role}
                                onChange={(e) => handleRoleUpdate(member.user._id, e.target.value)}
                                className="bg-transparent border-none text-[10px] font-black uppercase tracking-widest text-orange-500 outline-none cursor-pointer"
                              >
                                <option value="admin">Admin</option>
                                <option value="editor">Editor</option>
                                <option value="viewer">Viewer</option>
                              </select>
                            ) : (
                              <span className="text-[10px] font-black uppercase tracking-widest text-gray-700">{member.role}</span>
                            )}

                            {isAdmin && !isMe && (
                              <button onClick={() => openRemoveModal(member.user._id)} className="text-gray-700 hover:text-red-500 p-2">
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {isAdmin && <> {usage?.limits?.tokensPerProject > 0 ?
                    <div className="pt-8 border-t border-white/5">
                      <ServiceTokens projectId={id} />
                    </div>
                    :
                    <div className="relative overflow-hidden rounded-[2.5rem] border border-white/5 bg-[#0a0a0a] p-8 md:p-12 text-center">
                      {/* Subtle Background Glow */}
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-orange-600/10 rounded-full blur-[80px] pointer-events-none" />

                      <div className="relative z-10 max-w-md mx-auto">
                        <div className="w-16 h-16 bg-orange-500/10 border border-orange-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6 text-orange-500">
                          <Zap size={32} className="fill-orange-500/20" />
                        </div>

                        <h3 className="text-xl md:text-2xl font-black text-white tracking-tighter mb-3 uppercase">
                          Automate your <span className="text-orange-500">Pipeline</span>
                        </h3>

                        <p className="text-sm text-gray-500 leading-relaxed mb-8">
                          Service Tokens allow your CI/CD, Docker, and Production servers to fetch secrets automatically. This feature is exclusive to <span className="text-white font-bold">Pro</span> teams.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                          <Link to="/dashboard/subscription" className="flex-1 sm:flex-none">
                            <Button variant="primary" className="w-full px-8 py-3 bg-orange-600 hover:bg-orange-500 text-white font-black uppercase text-[11px] tracking-widest flex items-center justify-center gap-2">
                              Upgrade to Pro <ArrowRight size={14} />
                            </Button>
                          </Link>
                          <Link to="/dashboard/docs" className="flex-1 sm:flex-none">
                            <Button variant="secondary" className="w-full px-8 py-3 border border-white/10 text-gray-400 hover:text-white font-black uppercase text-[11px] tracking-widest">
                              View Docs
                            </Button>
                          </Link>
                        </div>

                        <div className="mt-10 pt-8 border-t border-white/5 flex items-center justify-center gap-6">
                          <div className="flex items-center gap-2 text-[10px] text-gray-600 font-bold uppercase tracking-widest">
                            <Shield size={14} /> Encrypted Access
                          </div>
                          <div className="flex items-center gap-2 text-[10px] text-gray-600 font-bold uppercase tracking-widest">
                            <RefreshCw size={14} /> Instant Sync
                          </div>
                        </div>
                      </div>
                    </div>
                  }</>
                  }
                </div>
              )}
              {activeTab === 'logs' && <div className="p-2"><AuditLogs projectId={id} /></div>}
            </>)}
        </div>

        {/* --- Modals --- */}

        {/* INTEGRATION MODAL */}
        <Modal
          isOpen={isIntegrationModalOpen}
          onClose={() => setIntegrationModalOpen(false)}
          title={`Connect ${selectedProvider?.name}`}
        >
          {<Formik
            initialValues={{ envdock_env: 'dev', ...selectedProvider?.fields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}) }}
            // validationSchema={Yup.object().shape({
            //   envdock_env: Yup.string().required('Required')
            // })}
            validationSchema={Yup.object().shape({
              // 1. Static requirement for the environment mapping
              envdock_env: Yup.string().required('Environment mapping is required'),

              // 2. Dynamic requirements based on the selected provider's fields
              ...(selectedProvider?.fields || []).reduce((acc, field) => {
                acc[field.name] = field.optional
                  ? Yup.string() // Optional fields just need to be strings
                  : Yup.string().required(`${field.label} is required`); // Required fields get the rule
                return acc;
              }, {})
            })}
            onSubmit={handleSubmitIntegration}
          >
            {({ isSubmitting, errors, touched }) => (
              <Form className="space-y-6 ">
                <div className="bg-orange-500/5 border border-orange-500/20 p-4 rounded-xl flex gap-3">
                  <div className="mt-0.5 text-orange-500"><Activity size={16} /></div>
                  <div>
                    <h5 className="text-xs font-bold text-orange-500 mb-1">How this works</h5>
                    <p className="text-[10px] text-gray-400 leading-relaxed">
                      When you push changes to your <b>EnvDock</b> vault, we will automatically update the environment variables in <b>{selectedProvider?.name}</b>.
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black uppercase px-1 tracking-widest text-gray-500 ">
                    Map EnvDock Environment
                  </label>
                  <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-sm  text-orange-500`}>
                    Required
                  </span>
                  <Field
                    as="select"
                    name="envdock_env"
                    className={`w-full bg-[#0a0a0a] border rounded-xl px-4 py-3 text-sm text-white focus:outline-none transition-colors ${errors.envdock_env && touched.envdock_env ? 'border-red-500' : 'border-white/10 focus:border-orange-500'
                      }`}
                  >
                    {project?.environments.map(env => (
                      <option key={env} value={env}>{env.toUpperCase()}</option>
                    ))}
                  </Field>

                  {/* Error Message Display */}
                  {errors.envdock_env && touched.envdock_env && (
                    <div className="text-red-500 text-[10px] font-bold mt-1.5 uppercase tracking-wider">{errors.envdock_env}</div>
                  )}
                </div>

                {/* Dynamic Fields based on Provider */}
                {selectedProvider?.fields.map((field) => (
                  <div key={field.name}>
                    <label className="block text-[10px] px-1 font-black uppercase tracking-widest text-gray-500 ">
                      {field.label}
                    </label>
                    <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-sm ${field.optional ? ' text-gray-400' : ' text-orange-500'}`}>
                      {field.optional ? 'Optional' : 'Required'}
                    </span>
                    <Field
                      name={field.name}
                      type={field.type}
                      placeholder={field.placeholder}
                      className={`w-full bg-[#0a0a0a] border rounded-xl px-4 py-3 text-sm text-white font-mono focus:outline-none transition-colors ${errors[field.name] && touched[field.name] ? 'border-red-500' : 'border-white/10 focus:border-orange-500'
                        }`}
                    />
                    {field.helpText && (
                      <p className="text-[10px] text-gray-400/80 leading-relaxed px-2 py-0.5">
                        {field.helpText}
                      </p>
                    )}
                    {/* Error Message Display */}
                    {errors[field.name] && touched[field.name] && (
                      <div className="text-red-500 text-[10px] font-bold mt-1.5 uppercase tracking-wider">{errors[field.name]}</div>
                    )}
                  </div>
                ))}

                <Button type="submit" variant="primary" className="w-full h-12" disabled={isSubmitting}>
                  {isSubmitting ? "Connecting..." : "Enable Integration"}
                </Button>
              </Form>
            )}
          </Formik>

          }
        </Modal>

        <Modal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)} title="v.Timeline">
          <div className="space-y-3">
            {history.length === 0 ? <p className="text-gray-700 text-center py-10 font-black text-xs uppercase tracking-widest">No history available</p> :
              history.map((ver) => {
                const isActive = ver.version === currentVersion;
                return (
                  <div key={ver._id} className={`flex items-center justify-between p-4 rounded-2xl border ${isActive ? 'bg-orange-500/5 border-orange-500/20' : 'border-white/5'}`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-mono text-[10px] font-black ${isActive ? 'bg-orange-500 text-white' : 'bg-white/5 text-gray-500'}`}>
                        v{ver.version}
                      </div>
                      <div>
                        <p className="text-xs font-black text-white">Version {ver.version}</p>
                        <p className="text-[9px] text-gray-600 font-bold uppercase mt-0.5">{new Date(ver.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                    {!isActive && (
                      <button onClick={() => handleRollback(ver.version)} className="p-2 text-gray-500 hover:text-orange-500 transition-colors">
                        <RotateCcw size={16} />
                      </button>
                    )}
                  </div>
                );
              })}
          </div>
        </Modal>

        <Modal isOpen={isInviteOpen} onClose={() => setInviteOpen(false)} title="Provision Access">
          <Formik
            initialValues={{ email: '', role: 'viewer' }}
            validationSchema={Yup.object({ email: Yup.string().email('Invalid email').required('Required') })}
            onSubmit={handleInvite}
          >
            {({ isSubmitting }) => (
              <Form className="space-y-6">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-gray-600 mb-2">User Email</label>
                  <Field name="email" placeholder="dev@corp.sys" className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-orange-500 outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-gray-600 mb-2">Access Role</label>
                  <Field as="select" name="role" className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-orange-500 outline-none">
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </Field>
                </div>
                <Button type="submit" variant="primary" className="w-full h-12" disabled={isSubmitting}>
                  {isSubmitting ? "Adding..." : "Add Member"}
                </Button>
              </Form>
            )}
          </Formik>
        </Modal>

        <Modal
          isOpen={isConfirmOpen}
          onClose={() => setConfirmOpen(false)}
          title="Revoke Access"
        >
          <div className="space-y-6">
            <p className="text-sm text-gray-400">
              Are you sure you want to remove this member? They will lose all access to this project immediately.
            </p>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleConfirmRemove}
              >
                Remove Member
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={!!integrationToDelete}
          onClose={() => setIntegrationToDelete(null)}
          title="Delete Integration?"
          variant="danger"
        >
          <div className="space-y-6">
            <div className="bg-red-500/5 border border-red-500/10 p-5 rounded-2xl flex gap-4">
              <div className="p-3 bg-red-500/10 rounded-xl h-fit text-red-500">
                <AlertTriangle size={20} />
              </div>
              <div>
                <h4 className="text-red-500 font-bold text-sm uppercase tracking-wide mb-1">Warning: Irreversible Action</h4>
                <p className="text-gray-400 text-xs leading-relaxed">
                  Your upcoming updates will not be synced!
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                onClick={() => setIntegrationToDelete(null)}
                className="w-full"
              >
                Cancel
              </Button>
              <button
                onClick={handleIntegrationDelete}
                className="
                        flex items-center justify-center gap-2 w-full
                        bg-red-600 hover:bg-red-500 text-white
                        font-bold uppercase tracking-widest text-xs
                        py-4 rounded-xl transition-all
                      "
              >
                Confirm Deletion
              </button>
            </div>
          </div>
        </Modal>

        <Modal isOpen={!!showIntegrationLog} onClose={() => setShowIntegrationLog(null)} title="Error Log">
          <div className="space-y-3">
            {showIntegrationLog && <div className="text-red-500 text-sm mt-1 space-y-2">
              ⚠️ Sync Failed:<br/> {showIntegrationLog}
            </div>}
          </div>
        </Modal>
        <AnimatePresence>
          {isExportOpen && (
            <ExportModal
              isOpen={isExportOpen}
              onClose={() => setIsExportOpen(false)}
              secrets={secrets}
              envName={activeEnv}
            />
          )}
        </AnimatePresence>
      </div>
    </>
  );
}